import {
  AdditionalReference,
  CostLineItem,
  NormalizedLoad,
  RateData,
  ValueUnit,
} from 'types/Load';
import { TransportType } from 'types/QuoteRequest';
import { Maybe, MaybeUndef } from 'types/UtilityTypes';
import { resolveTransportType } from 'utils/loadInfoAndBuilding';

import { transportTypes } from './constants';

export function matchTaiCatalogTransportType(type: MaybeUndef<string>): string {
  const raw = String(type ?? '').trim();
  if (!raw) {
    return '';
  }

  return (
    transportTypes.find(
      (option) => option.toLowerCase() === raw.toLowerCase()
    ) ?? raw
  );
}

/** Map load/API codes (e.g. VAN) to TAI Select values for edit/post-create only. */
export function toTaiFormTransportType(type: MaybeUndef<string>): string {
  const raw = String(type ?? '').trim();
  if (!raw) {
    return '';
  }

  const catalogMatch = matchTaiCatalogTransportType(raw);
  if ((transportTypes as readonly string[]).includes(catalogMatch)) {
    return catalogMatch;
  }

  const resolved = resolveTransportType(raw);
  if (resolved === TransportType.FLATBED) {
    return 'Flatbed';
  }
  if (resolved === TransportType.REEFER) {
    return 'Reefer';
  }
  if (resolved === TransportType.VAN) {
    return 'Van';
  }

  return raw;
}

export function normalizeTaiFuelCarrierLineItems(
  carrierLineItems: CostLineItem[] = []
): CostLineItem[] {
  const fuelLineItem = carrierLineItems.find((item) => item?.label === 'Fuel');
  const fallbackFuelCharge = [...carrierLineItems]
    .reverse()
    .find((item) => typeof item?.totalChargeUSD === 'number')?.totalChargeUSD;

  return [
    {
      label: 'Fuel',
      unitBasis: fuelLineItem?.unitBasis || 'flat',
      quantity: fuelLineItem?.quantity ?? 1,
      ratePerUnitUSD: fuelLineItem?.ratePerUnitUSD ?? 0,
      totalChargeUSD: fuelLineItem?.totalChargeUSD ?? fallbackFuelCharge ?? 0,
      note: fuelLineItem?.note ?? '',
    },
  ];
}

export function normalizeTaiCarrierRateData(rateData: RateData): RateData {
  const carrierLineItems = normalizeTaiFuelCarrierLineItems(
    rateData.carrierLineItems ?? []
  );
  const carrierLineHaulCharge =
    Number(rateData.carrierLineHaulCharge?.val) || 0;
  const carrierFuelCharge = carrierLineItems[0]?.totalChargeUSD ?? 0;

  return {
    ...rateData,
    carrierLineItems,
    carrierCost: carrierLineHaulCharge + carrierFuelCharge,
  };
}

export function appendTaiPoNumsToCustomerRefNumber(
  refNumber: MaybeUndef<string>,
  poNums: MaybeUndef<string>
): string {
  const refs = (refNumber || '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);

  (poNums || '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)
    .forEach((po) => {
      if (!refs.includes(po)) {
        refs.push(po);
      }
    });

  return refs.join(',');
}

export function normalizeTaiTotalVolume(
  totalVolume: Maybe<ValueUnit>
): Maybe<ValueUnit> {
  return totalVolume?.val || totalVolume?.unit ? totalVolume : null;
}

const emptyTaiAdditionalReference = (): AdditionalReference => ({
  qualifier: '',
  number: '',
  weight: 0,
  pieces: 0,
  shouldSendToDriver: false,
});

/** Pad empty stop refs so Additional References UI doesn't replace() on mount and dirty fields. */
export function padTaiAdditionalReferences(
  refs: MaybeUndef<AdditionalReference[]>
): AdditionalReference[] {
  if (refs && refs.length > 0) {
    return refs;
  }
  return [emptyTaiAdditionalReference(), emptyTaiAdditionalReference()];
}

export function normalizeTaiLoadForForm(load: NormalizedLoad): NormalizedLoad {
  return {
    ...load,
    customer: {
      ...load.customer,
      refNumber: appendTaiPoNumsToCustomerRefNumber(
        load.customer?.refNumber,
        load.poNums
      ),
    },
    specifications: {
      ...load.specifications,
      totalVolume: normalizeTaiTotalVolume(load.specifications?.totalVolume),
    },
    rateData: normalizeTaiCarrierRateData(load.rateData),
    pickup: {
      ...load.pickup,
      additionalReferences: padTaiAdditionalReferences(
        load.pickup?.additionalReferences
      ),
    },
    consignee: {
      ...load.consignee,
      additionalReferences: padTaiAdditionalReferences(
        load.consignee?.additionalReferences
      ),
    },
  };
}
